import { useState } from 'react';
import { roleLabels } from '@/services/roleLabels';
import { useTranslation } from 'react-i18next';
import { Building2, Check, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/authStore';
import { showErrorToast } from '@/lib/errorToast';
import { cn } from '@/lib/utils';


/**
 * ENG-9 : changer d'organisation recharge la page. Les drapeaux d'interface
 * sont figés par chargement (lib/flags, DES-6) et doivent suivre la nouvelle
 * organisation ; les caches repartent de zéro. Refusé pendant un appel
 * (`inCall`), qu'un rechargement couperait.
 */
export function OrganisationSwitcher({ className, inCall = false }: { className?: string; inCall?: boolean }) {
  const { t } = useTranslation(['admin', 'settings']);
  const ROLE_LABELS = roleLabels(t);
  const user = useAuthStore((s) => s.user);
  const active = useAuthStore((s) => s.activeMembership);
  const memberships = useAuthStore((s) => s.availableMemberships);
  const switchOrganisation = useAuthStore((s) => s.switchOrganisation);
  const [switchingId, setSwitchingId] = useState<string | null>(null);

  if (!user) return null;
  if (!user.isSuperAdmin && memberships.length <= 1) return null;

  const triggerLabel = active?.organisationName ?? t('admin:organisationSwitcher.noActiveOrg');
  const triggerRole = active ? ROLE_LABELS[active.role] ?? active.role : null;

  async function handleSwitch(organisationId: string) {
    if (active?.organisationId === organisationId) return;
    setSwitchingId(organisationId);
    try {
      await switchOrganisation(organisationId);
      // The server set the new organisation's cookies: a full reload starts
      // over with its flags and an empty cache.
      window.location.reload();
      return;
    } catch (err) {
      showErrorToast(err);
    } finally {
      setSwitchingId(null);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('gap-2 max-w-[220px]', className)}
          disabled={inCall}
          title={inCall ? t('admin:organisationSwitcher.inCall') : undefined}
        >
          <Building2 className="size-4 shrink-0" />
          <span className="truncate">{triggerLabel}</span>
          {triggerRole && (
            // QA-W6-1: the role gives way to the organisation's name on a phone.
            <span className="hidden sm:inline text-xs text-muted-foreground shrink-0">
              · {triggerRole}
            </span>
          )}
          <ChevronDown className="size-4 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>{t('admin:organisationSwitcher.label')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {memberships.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">
            {t('admin:organisationSwitcher.empty')}
          </div>
        ) : (
          memberships.map((m) => {
            const isActive = m.organisationId === active?.organisationId;
            const isSwitching = switchingId === m.organisationId;
            return (
              <DropdownMenuItem
                key={m.id}
                disabled={isSwitching}
                onSelect={(e) => {
                  e.preventDefault();
                  handleSwitch(m.organisationId);
                }}
                className="flex items-center gap-2"
              >
                {isSwitching ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : isActive ? (
                  <Check className="size-4 text-primary" />
                ) : (
                  <span className="size-4" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="truncate">{m.organisationName}</div>
                  <div className="text-xs text-muted-foreground">
                    {ROLE_LABELS[m.role] ?? m.role}
                  </div>
                </div>
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
